import React, { useEffect, useRef, useState } from "react";

import {
  LOCATION_OPTIONS,
  NEW_YORK_STATE,
  VIDEO_CALL_LOCATION,
} from "~/shared/utils/constants";
import { useSignUpStore } from "~/stores";
import { tw } from "~/utils";
import { ChevronDownIcon } from "../icons/ChevronDownIcon";

interface LocationDropdownProps {
  selectedLocation: string;
  onLocationChange: (location: string) => void;
}

const LocationDropdown: React.FC<LocationDropdownProps> = ({
  selectedLocation = "",
  onLocationChange,
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const { patient } = useSignUpStore();
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const isNewYorkState = patient.state === NEW_YORK_STATE;

  const handleLocationChange = (location: string) => {
    onLocationChange(location);
    setIsOpen(false);
  };

  const displayText =
    selectedLocation === VIDEO_CALL_LOCATION
      ? "Telehealth"
      : selectedLocation || "Select location";

  return (
    <div className="relative w-full" ref={dropdownRef}>
      <button
        type="button"
        className="flex w-full cursor-pointer items-center justify-between rounded-lg border border-gray-300 bg-white px-4 py-3 text-left text-sm font-medium hover:bg-gray-50"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
      >
        <span className="truncate text-sm font-medium">{displayText}</span>
        <ChevronDownIcon
          className={tw(
            "ml-2 size-4 shrink-0 text-gray-900 transition-transform",
            isOpen && "rotate-180",
          )}
        />
      </button>

      {isOpen && (
        <div className="absolute right-0 left-0 z-20 mt-1.5 rounded-lg border border-gray-300 bg-white p-4 shadow-lg sm:right-0 sm:left-auto sm:w-64">
          {isNewYorkState && (
            <div>
              <h3 className="mb-2 text-base font-medium">New York</h3>
            </div>
          )}
          <div
            className={tw(
              "border-t border-gray-200 pt-2",
              !isNewYorkState && "border-none pt-0",
            )}
          >
            <div className="mt-2 flex flex-col space-y-3">
              {isNewYorkState ? (
                LOCATION_OPTIONS.map((location) => (
                  <div key={location} className="flex items-center">
                    <input
                      type="radio"
                      id={`location-${location}`}
                      name="location"
                      value={location}
                      checked={selectedLocation === location}
                      onChange={() => handleLocationChange(location)}
                      className="size-4 rounded accent-slate-700"
                    />
                    <label
                      htmlFor={`location-${location}`}
                      className="ml-2 text-base font-medium text-gray-900"
                    >
                      {location === VIDEO_CALL_LOCATION
                        ? "Telehealth"
                        : location}
                    </label>
                  </div>
                ))
              ) : (
                <div className="-mt-2 flex items-center">
                  <input
                    type="radio"
                    id={`location-${VIDEO_CALL_LOCATION}`}
                    name="location"
                    value={VIDEO_CALL_LOCATION}
                    checked={true}
                    disabled
                    className="size-4 rounded accent-slate-700"
                  />
                  <label
                    htmlFor={`location-${VIDEO_CALL_LOCATION}`}
                    className="ml-2 text-base font-medium text-gray-900"
                  >
                    Telehealth
                  </label>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default LocationDropdown;
